<script setup lang="ts">
import { isAppLocale } from '~/constants/appLocale'
import type { MarketingConsentSource } from '~/constants/marketingConsent'
import type { CVData } from '~/types'

/**
 * "E-mail me my CV" under a CV made without an account.
 *
 * The CV otherwise lives only in this tab, so a link to it by e-mail is a real
 * service, and the natural moment to ask for the address. The address the AI
 * found in the CV is filled in, but nothing happens until the visitor sends
 * the form. The first e-mail only confirms the address; the CV link follows
 * once its button is clicked. The marketing consent is a separate, unticked
 * box: the CV link is sent either way (see server/api/guest-cv/email.post.ts).
 *
 * `gate`: the same form in the dialog before the free download
 * (LandingResultOffers). No "no, thanks" and no frame of its own; once the
 * address is sent it emits `sent`, and the download waits for the click on the
 * link in the e-mail. Where we cannot send (today's e-mail limit, a CV no
 * longer stored) it emits `unsendable` and the download goes ahead all the
 * same: the visitor did their part.
 *
 * `purpose: 'save'`: the same gate in front of "save and edit later". The CV
 * is kept for the visitor by its link in the e-mail, and an account can follow.
 */
const props = defineProps<{
  cvData: CVData
  /** `guest_cv_generations` id; without it there is nothing to link to. */
  generationId: string
  template?: string
  consentSource: Extract<MarketingConsentSource, 'cv-chat' | 'cv-paste'>
  gate?: boolean
  /** What the gate is in front of: the free download, or keeping the CV to edit later. */
  purpose?: 'download' | 'save'
}>()

const emit = defineEmits<{
  /** The confirmation link went out to this address. */
  sent: [email: string]
  /** `gate`: it cannot go out through no fault of theirs, so the download should not wait for it. */
  unsendable: []
}>()

const { t, locale } = useI18n()
const { consentText } = useMarketingConsent()
const journey = useJourney()

const email = ref(props.cvData.contact?.email?.trim() || '')
const marketing = ref(false)
const state = ref<'idle' | 'sending' | 'sent' | 'declined'>('idle')
const sentTo = ref('')
const errorMsg = ref('')

// A new CV (start over, another answer set) is a new question.
watch(() => props.generationId, () => {
  state.value = 'idle'
  errorMsg.value = ''
  email.value = props.cvData.contact?.email?.trim() || ''
})

const sentMessage = computed(() => t('landing.emailCv.sent', { email: sentTo.value }))

async function send() {
  errorMsg.value = ''
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
    errorMsg.value = t('common.validators.email')
    return
  }
  state.value = 'sending'
  try {
    await $fetch('/api/guest-cv/email', {
      method: 'POST',
      body: {
        generationId: props.generationId,
        email: email.value.trim(),
        template: props.template,
        locale: isAppLocale(locale.value) ? locale.value : 'en',
        marketing: marketing.value,
        source: props.consentSource,
      },
    })
    sentTo.value = email.value.trim()
    state.value = 'sent'
    journey.track('cv_email_request', { generation_id: props.generationId, gate: !!props.gate, purpose: props.purpose ?? 'download', marketing: marketing.value })
    emit('sent', sentTo.value)
    // Asked and answered: the newsletter dialog after the download has nothing to add.
    if (marketing.value) {
      try {
        localStorage.setItem('newsletter_subscribed', 'true')
      } catch {
        // Blocked storage: the dialog may still show once, which is harmless.
      }
    }
  } catch (err) {
    const fetchError = err as { statusCode?: number, data?: { data?: { code?: string } } }
    state.value = 'idle'
    if (props.gate && (fetchError.statusCode === 404 || fetchError.statusCode === 429)) {
      emit('unsendable')
      return
    }
    errorMsg.value = fetchError.statusCode === 404
      ? t('landing.emailCv.expired')
      : fetchError.statusCode === 429
        ? t('landing.emailCv.dailyLimit')
        : t('landing.emailCv.error')
  }
}
</script>

<template>
  <div v-if="state !== 'declined'" :class="gate ? '' : 'rounded-[0.625rem] border border-line bg-white p-5 shadow-card dark:border-gray-700 dark:bg-gray-900'">
    <div v-if="!gate" class="mb-1 flex items-center gap-2 font-title text-lg font-bold text-ink dark:text-white">
      <i class="bi bi-envelope-paper text-accent" aria-hidden="true" />
      {{ t('landing.emailCv.title') }}
    </div>

    <SofaxNotice v-if="state === 'sent'" type="success" plain>{{ sentMessage }}</SofaxNotice>

    <form v-else class="grid gap-3" @submit.prevent="send">
      <p class="!text-sm !text-body dark:!text-gray-300">{{ purpose === 'save' ? t('landing.emailCv.saveText') : gate ? t('landing.emailCv.gateText') : t('landing.emailCv.text') }}</p>
      <label class="sr-only" for="email-cv-address">{{ t('landing.emailCv.emailLabel') }}</label>
      <SofaxInput
        id="email-cv-address"
        v-model="email"
        type="email"
        autocomplete="email"
        inputmode="email"
        :placeholder="t('newsletter.emailPlaceholder')"
        :disabled="state === 'sending'"
      />
      <SofaxCheckbox id="email-cv-marketing" v-model="marketing" :disabled="state === 'sending'">
        <span class="!text-sm !text-body dark:!text-gray-300">{{ consentText }}</span>
      </SofaxCheckbox>
      <SofaxNotice v-if="errorMsg" type="error">{{ errorMsg }}</SofaxNotice>
      <div class="flex flex-wrap items-center gap-3">
        <SofaxBtn
          type="submit"
          variant="dark"
          :disabled="state === 'sending'"
:block="gate"
          :label="state === 'sending' ? t('landing.emailCv.sending') : purpose === 'save' ? t('landing.emailCv.saveSend') : gate ? t('landing.emailCv.gateSend') : t('landing.emailCv.send')"
        />
        <button v-if="!gate" type="button" class="link !text-sm" :disabled="state === 'sending'" @click="state = 'declined'">
          {{ t('landing.emailCv.decline') }}
        </button>
      </div>
    </form>
  </div>
</template>
